import axiosInstance from './axiosInstance'

export type TaskStatus   = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED'
export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'

export interface TaskAssignedTo {
  id:        string
  name:      string
  email:     string
  avatarUrl: string | null
}

export interface Task {
  id:         string
  leadId?:    string | null
  leadName?:  string | null
  dealId?:    string | null
  dealName?:  string | null
  source?:    'LEAD' | 'DEAL' | string
  title:      string
  description?: string | null
  priority:   TaskPriority
  dueDate:    string | null
  status:     TaskStatus
  assignedTo: TaskAssignedTo | null
  createdAt:  string
  updatedAt:  string
}

// Matches the actual PagedResult<T> shape the backend returns
interface ApiPagedResult<T> {
  results:     T[]
  rowCount:    number
  pageSize:    number
  currentPage: number
}

export interface PaginatedTasks {
  items:      Task[]
  total:      number
  page:       number
  limit:      number
  totalPages: number
}

export interface CreateTaskPayload {
  leadId:        string
  title:         string
  description?:  string
  dueDate?:      string
  assignedToId?: string
}

// ── Summary ───────────────────────────────────────────────────────────────────
export interface TasksSummary {
  pendingCount:   number  // OPEN + PENDING + IN_PROGRESS
  overdueCount:   number  // pending AND past due date
  completedCount: number  // DONE / COMPLETED
  totalCount:     number
}

export const tasksApi = {
  getMyTasks: async (params?: {
    status?: string
    search?: string
    page?: number
    pageSize?: number
    orgIds?: string[]
    lobIds?: string[]
    ownerIds?: string[]
    priorities?: string[]
  }): Promise<PaginatedTasks> => {
    const res = await axiosInstance.get<{ data: ApiPagedResult<Task> }>('/tasks/my', { params })
    const d = res.data.data
    const pageSize = d.pageSize || params?.pageSize || 200
    return {
      items:      d.results ?? [],
      total:      d.rowCount ?? 0,
      page:       d.currentPage ?? 1,
      limit:      pageSize,
      totalPages: pageSize > 0 ? Math.ceil((d.rowCount ?? 0) / pageSize) : 1,
    }
  },

  complete: async (id: string): Promise<void> => {
    await axiosInstance.patch(`/tasks/${id}/complete`)
  },

  create: async (payload: CreateTaskPayload): Promise<Task> => {
    const res = await axiosInstance.post<{ data: Task }>('/tasks', payload)
    return res.data.data
  },

  updateStatus: async (id: string, status: TaskStatus): Promise<void> => {
    await axiosInstance.patch(`/tasks/${id}/status`, { status })
  },

  getMySummary: async (params?: {
    search?: string
    orgIds?: string[]
    lobIds?: string[]
    ownerIds?: string[]
    priorities?: string[]
  }): Promise<TasksSummary> => {
    const res = await axiosInstance.get<{ data: TasksSummary }>('/tasks/my/summary', { params })
    return res.data.data
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/tasks/${id}`)
  },
}
